import type { Identify } from '@amplitude/analytics-browser';
import * as amplitude from '@amplitude/analytics-browser';

import type { AnalyticEvents } from '@repo/types/analytic-events/analytic-events';
import type { AnalyticProvider, Screen, ValidPropertyType } from './analytics';
import { snakeCaseObject } from './utils';

export class Amplitude implements AnalyticProvider {
    private userId: string | null = null;
    private enabled: boolean = false;
    private apiKey: string;
    private platform: string = 'web';

    #identifyEvent: Identify | null = null;

    constructor(apiKey: string, userId: string | null, enabled: boolean, platform: string) {
        this.apiKey = apiKey;
        this.userId = userId;
        this.enabled = enabled;
        this.platform = platform;
    }

    public initialize(): void {
        amplitude.init(this.apiKey, this.userId ?? undefined, {
            optOut: !this.enabled,
            appVersion: process.env.NEXT_PUBLIC_BUILD_ID,
            autocapture: false,
        });

        this.#identifyEvent = new amplitude.Identify();

        amplitude.identify(this.#identifyEvent);
    }

    public track<T extends keyof AnalyticEvents>(event: T, params: AnalyticEvents[T]['params']): void {
        const eventParams = typeof params === 'object' ? snakeCaseObject(params) : {};

        amplitude.track(event, eventParams, {
            platform: this.platform,
        });
    }

    public setUserProperty(name: string, value: ValidPropertyType): void {
        if (!this.#identifyEvent) {
            return;
        }

        this.#identifyEvent.set(name, value);
    }

    public setUserId(userId: string | null): void {
        if (this.userId === userId) {
            return;
        }

        this.userId = userId;
        amplitude.setUserId(userId ?? undefined);
    }

    public screenView(screen: Screen): void {
        amplitude.track('screen_view', {
            screen_name: screen.name,
            screen_class: screen.class,
            ...(screen.params || null),
        });
    }
}
